import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Trash2,
  RotateCw,
  Save,
  Grid,
  Plus,
  Minus,
  Eye,
  Edit3,
  Check,
  Copy,
  Undo2,
  Redo2,
  Sparkles,
  Monitor,
  Armchair,
  BookOpen,
  Server,
  Lamp,
  Flower2,
  Frame,
  Coffee,
  Cat,
  Package,
} from 'lucide-react';
// Note: FURNITURE_CATEGORIES, ASSET_PACKS, OFFICE_PRESETS, and FLOOR_THEMES are defined locally below

// Furniture categories for the office builder
const FURNITURE_CATEGORIES = {
  desks: {
    name: 'Desks',
    icon: Monitor,
    items: [
      { id: 'desk-modern', name: 'Modern Desk', emoji: '🖥️', width: 80, height: 50, zOffset: 0 },
      { id: 'desk-wooden', name: 'Wooden Desk', emoji: '📝', width: 80, height: 50, zOffset: 0 },
      { id: 'desk-corner', name: 'Corner Desk', emoji: '💻', width: 90, height: 60, zOffset: 0 },
    ],
  },
  seating: {
    name: 'Seating',
    icon: Armchair,
    items: [
      { id: 'chair-office', name: 'Office Chair', emoji: '🪑', width: 30, height: 30, zOffset: 10 },
      { id: 'chair-gaming', name: 'Gaming Chair', emoji: '🎮', width: 35, height: 35, zOffset: 10 },
      { id: 'sofa-small', name: 'Small Sofa', emoji: '🛋️', width: 70, height: 40, zOffset: 0 },
      { id: 'sofa-large', name: 'Large Sofa', emoji: '🛋️', width: 100, height: 50, zOffset: 0 },
      { id: 'armchair', name: 'Armchair', emoji: '💺', width: 45, height: 45, zOffset: 0 },
    ],
  },
  storage: {
    name: 'Storage',
    icon: BookOpen,
    items: [
      { id: 'bookshelf', name: 'Bookshelf', emoji: '📚', width: 50, height: 70, zOffset: -20 },
      { id: 'cabinet', name: 'Filing Cabinet', emoji: '🗄️', width: 40, height: 50, zOffset: -20 },
      { id: 'shelf-small', name: 'Wall Shelf', emoji: '📦', width: 60, height: 20, zOffset: -30 },
    ],
  },
  tech: {
    name: 'Tech',
    icon: Server,
    items: [
      { id: 'tv-large', name: 'Large TV', emoji: '📺', width: 100, height: 60, zOffset: -30 },
      { id: 'tv-small', name: 'Small TV', emoji: '📺', width: 60, height: 40, zOffset: -30 },
      { id: 'monitor', name: 'Monitor', emoji: '🖥️', width: 45, height: 35, zOffset: 20 },
      { id: 'server-rack', name: 'Server Rack', emoji: '🖧', width: 45, height: 80, zOffset: 0 },
      { id: 'printer', name: 'Printer', emoji: '🖨️', width: 40, height: 30, zOffset: 0 },
      { id: 'whiteboard', name: 'Whiteboard', emoji: '📋', width: 80, height: 60, zOffset: -30 },
      { id: 'projector', name: 'Projector', emoji: '📽️', width: 30, height: 25, zOffset: -50 },
    ],
  },
  lighting: {
    name: 'Lighting',
    icon: Lamp,
    items: [
      { id: 'lamp-floor', name: 'Floor Lamp', emoji: '🪔', width: 25, height: 60, zOffset: 0 },
      { id: 'lamp-desk', name: 'Desk Lamp', emoji: '💡', width: 20, height: 25, zOffset: 20 },
      { id: 'lamp-ceiling', name: 'Ceiling Light', emoji: '💡', width: 40, height: 20, zOffset: -50 },
    ],
  },
  plants: {
    name: 'Plants & Trees',
    icon: Flower2,
    items: [
      { id: 'tree-large', name: 'Large Tree', emoji: '🌳', width: 60, height: 90, zOffset: 0 },
      { id: 'tree-palm', name: 'Palm Tree', emoji: '🌴', width: 50, height: 85, zOffset: 0 },
      { id: 'tree-pine', name: 'Pine Tree', emoji: '🌲', width: 45, height: 80, zOffset: 0 },
      { id: 'tree-christmas', name: 'Christmas Tree', emoji: '🎄', width: 50, height: 75, zOffset: 0 },
      { id: 'plant-small', name: 'Small Plant', emoji: '🪴', width: 25, height: 30, zOffset: 0 },
      { id: 'plant-medium', name: 'Medium Plant', emoji: '🌿', width: 35, height: 45, zOffset: 0 },
      { id: 'cactus', name: 'Cactus', emoji: '🌵', width: 20, height: 35, zOffset: 0 },
      { id: 'flower-bouquet', name: 'Flower Bouquet', emoji: '💐', width: 30, height: 35, zOffset: 10 },
      { id: 'bonsai', name: 'Bonsai', emoji: '🪵', width: 25, height: 30, zOffset: 10 },
    ],
  },
  decor: {
    name: 'Decor',
    icon: Frame,
    items: [
      { id: 'rug-small', name: 'Small Rug', emoji: '🟫', width: 60, height: 40, zOffset: -100 },
      { id: 'rug-large', name: 'Large Rug', emoji: '🟫', width: 100, height: 70, zOffset: -100 },
      { id: 'poster', name: 'Poster', emoji: '🖼️', width: 40, height: 50, zOffset: -30 },
      { id: 'clock', name: 'Wall Clock', emoji: '🕐', width: 25, height: 25, zOffset: -30 },
      { id: 'photo-frame', name: 'Photo Frame', emoji: '📷', width: 20, height: 25, zOffset: -30 },
    ],
  },
  refreshments: {
    name: 'Refreshments',
    icon: Coffee,
    items: [
      { id: 'coffee-machine', name: 'Coffee Machine', emoji: '☕', width: 35, height: 45, zOffset: 0 },
      { id: 'water-cooler', name: 'Water Cooler', emoji: '🚰', width: 30, height: 50, zOffset: 0 },
      { id: 'vending', name: 'Vending Machine', emoji: '🥤', width: 45, height: 70, zOffset: 0 },
      { id: 'mini-fridge', name: 'Mini Fridge', emoji: '🧊', width: 35, height: 45, zOffset: 0 },
    ],
  },
  pets: {
    name: 'Pets',
    icon: Cat,
    items: [
      { id: 'cat-bed', name: 'Cat + Bed', emoji: '🐱', width: 35, height: 25, zOffset: 0, animated: true },
      { id: 'dog-bed', name: 'Dog + Bed', emoji: '🐕', width: 40, height: 30, zOffset: 0, animated: true },
      { id: 'fish-tank', name: 'Fish Tank', emoji: '🐠', width: 50, height: 40, zOffset: 0, animated: true },
      { id: 'bunny-bed', name: 'Bunny Corner', emoji: '🐰', width: 36, height: 26, zOffset: 0, animated: true },
      { id: 'hamster-home', name: 'Hamster Home', emoji: '🐹', width: 34, height: 28, zOffset: 0, animated: true },
      { id: 'bird-perch', name: 'Bird Perch', emoji: '🦜', width: 30, height: 42, zOffset: -10, animated: true },
      { id: 'turtle-pond', name: 'Turtle Pond', emoji: '🐢', width: 44, height: 30, zOffset: 0, animated: true },
    ],
  },
  structural: {
    name: 'Rooms',
    icon: Grid,
    items: [
      { id: 'divider-short', name: 'Room Divider', emoji: '🧱', width: 90, height: 16, zOffset: -10 },
      { id: 'divider-wide', name: 'Glass Wall', emoji: '🪟', width: 130, height: 16, zOffset: -10 },
      { id: 'window-wide', name: 'Wide Window', emoji: '🪟', width: 70, height: 28, zOffset: -35 },
      { id: 'meeting-table', name: 'Meeting Table', emoji: '🪑', width: 110, height: 60, zOffset: 0 },
      { id: 'break-counter', name: 'Break Counter', emoji: '🍱', width: 90, height: 40, zOffset: 0 },
      { id: 'reception-desk', name: 'Reception Desk', emoji: '🛎️', width: 120, height: 50, zOffset: 0 },
      { id: 'door-single', name: 'Door', emoji: '🚪', width: 40, height: 60, zOffset: -35 },
      { id: 'stairs', name: 'Stairs', emoji: '🪜', width: 60, height: 80, zOffset: -20 },
    ],
  },
  electronics: {
    name: 'Electronics',
    icon: Monitor,
    items: [
      { id: 'laptop', name: 'Laptop', emoji: '💻', width: 30, height: 25, zOffset: 20 },
      { id: 'phone-desk', name: 'Desk Phone', emoji: '☎️', width: 20, height: 20, zOffset: 20 },
      { id: 'tablet', name: 'Tablet', emoji: '📱', width: 20, height: 28, zOffset: 20 },
      { id: 'headset', name: 'Headset', emoji: '🎧', width: 25, height: 25, zOffset: 20 },
      { id: 'speaker', name: 'Speaker', emoji: '🔊', width: 30, height: 40, zOffset: 0 },
      { id: 'router', name: 'Router', emoji: '📡', width: 25, height: 20, zOffset: 0 },
      { id: 'camera', name: 'Security Cam', emoji: '📷', width: 20, height: 20, zOffset: -40 },
    ],
  },
  sports: {
    name: 'Recreation',
    icon: Frame,
    items: [
      { id: 'ping-pong', name: 'Ping Pong', emoji: '🏓', width: 100, height: 60, zOffset: 0 },
      { id: 'foosball', name: 'Foosball', emoji: '⚽', width: 90, height: 50, zOffset: 0 },
      { id: 'arcade', name: 'Arcade Machine', emoji: '🕹️', width: 50, height: 70, zOffset: 0 },
      { id: 'dart-board', name: 'Dart Board', emoji: '🎯', width: 35, height: 35, zOffset: -35 },
      { id: 'basketball-hoop', name: 'Mini Hoop', emoji: '🏀', width: 40, height: 50, zOffset: -30 },
      { id: 'gym-bike', name: 'Exercise Bike', emoji: '🚴', width: 45, height: 50, zOffset: 0 },
      { id: 'yoga-mat', name: 'Yoga Mat', emoji: '🧘', width: 60, height: 25, zOffset: -100 },
    ],
  },
  seasonal: {
    name: 'Seasonal',
    icon: Sparkles,
    items: [
      { id: 'christmas-tree', name: 'Christmas Tree', emoji: '🎄', width: 50, height: 80, zOffset: 0 },
      { id: 'menorah', name: 'Menorah', emoji: '🕎', width: 35, height: 40, zOffset: 10 },
      { id: 'pumpkin', name: 'Pumpkin', emoji: '🎃', width: 30, height: 30, zOffset: 0 },
      { id: 'ghost', name: 'Ghost Decor', emoji: '👻', width: 30, height: 40, zOffset: 0, animated: true },
      { id: 'heart-balloon', name: 'Heart Balloon', emoji: '💝', width: 25, height: 40, zOffset: 0, animated: true },
      { id: 'fireworks', name: 'Fireworks', emoji: '🎆', width: 40, height: 50, zOffset: -40, animated: true },
      { id: 'snowman', name: 'Snowman', emoji: '⛄', width: 35, height: 50, zOffset: 0 },
    ],
  },
  food: {
    name: 'Food & Dining',
    icon: Coffee,
    items: [
      { id: 'dining-table', name: 'Dining Table', emoji: '🍽️', width: 80, height: 50, zOffset: 0 },
      { id: 'bar-counter', name: 'Bar Counter', emoji: '🍺', width: 100, height: 35, zOffset: 0 },
      { id: 'stool', name: 'Bar Stool', emoji: '🪑', width: 25, height: 35, zOffset: 10 },
      { id: 'pizza-box', name: 'Pizza Box', emoji: '🍕', width: 25, height: 25, zOffset: 15 },
      { id: 'fruit-bowl', name: 'Fruit Bowl', emoji: '🍎', width: 25, height: 20, zOffset: 15 },
      { id: 'cake', name: 'Celebration Cake', emoji: '🎂', width: 30, height: 30, zOffset: 15 },
      { id: 'sushi-platter', name: 'Sushi Platter', emoji: '🍣', width: 35, height: 25, zOffset: 15 },
    ],
  },
  vehicles: {
    name: 'Vehicles',
    icon: Package,
    items: [
      { id: 'scooter', name: 'Scooter', emoji: '🛴', width: 35, height: 45, zOffset: 0 },
      { id: 'bicycle', name: 'Bicycle', emoji: '🚲', width: 50, height: 40, zOffset: 0 },
      { id: 'skateboard', name: 'Skateboard', emoji: '🛹', width: 40, height: 15, zOffset: -100 },
      { id: 'segway', name: 'Segway', emoji: '🛵', width: 30, height: 45, zOffset: 0 },
    ],
  },
};

const ASSET_PACKS = [
  {
    id: 'starter',
    name: 'Starter Office Pack',
    description: 'Core desks, chairs, shelves, and utility pieces.',
    categories: ['desks', 'seating', 'storage', 'tech'],
    priceHint: '$19 add-on',
  },
  {
    id: 'lush',
    name: 'Lush Decor Pack',
    description: 'Plants, rugs, wall art, and cozy lounge props.',
    categories: ['plants', 'decor', 'refreshments'],
    priceHint: '$24 add-on',
  },
  {
    id: 'premium',
    name: 'Premium Ops Pack',
    description: 'Server racks, meeting rooms, glass dividers, and team spaces.',
    categories: ['tech', 'structural', 'refreshments'],
    priceHint: '$39 add-on',
  },
  {
    id: 'gaming',
    name: 'Gaming Lounge Pack',
    description: 'Arcade machines, gaming chairs, ping pong, and recreation gear.',
    categories: ['sports', 'seating', 'electronics'],
    priceHint: '$29 add-on',
  },
  {
    id: 'wellness',
    name: 'Wellness & Fitness Pack',
    description: 'Yoga mats, exercise bikes, meditation corners, and zen decor.',
    categories: ['sports', 'plants', 'decor'],
    priceHint: '$22 add-on',
  },
  {
    id: 'seasonal',
    name: 'Seasonal Celebration Pack',
    description: 'Holiday decorations, festive props, and celebration items.',
    categories: ['seasonal', 'decor', 'food'],
    priceHint: '$15 add-on',
  },
  {
    id: 'executive',
    name: 'Executive Suite Pack',
    description: 'Luxury furniture, reception desks, and premium meeting spaces.',
    categories: ['structural', 'seating', 'decor'],
    priceHint: '$49 add-on',
  },
  {
    id: 'cafe',
    name: 'Cafe & Kitchen Pack',
    description: 'Dining tables, bar counters, food displays, and beverages.',
    categories: ['food', 'refreshments', 'seating'],
    priceHint: '$27 add-on',
  },
];

const OFFICE_PRESETS = [
  {
    id: 'support-pod',
    name: 'Support Pod',
    description: 'Compact customer support floor with a coffee corner.',
    floorType: 'tile',
    items: [
      { id: 'preset-desk-1', type: 'desk-modern', x: 120, y: 145, rotation: 0 },
      { id: 'preset-desk-2', type: 'desk-modern', x: 260, y: 145, rotation: 0 },
      { id: 'preset-desk-3', type: 'desk-modern', x: 120, y: 285, rotation: 0 },
      { id: 'preset-desk-4', type: 'desk-modern', x: 260, y: 285, rotation: 0 },
      { id: 'preset-rug-1', type: 'rug-large', x: 470, y: 305, rotation: 0 },
      { id: 'preset-sofa-1', type: 'sofa-small', x: 500, y: 330, rotation: 0 },
      { id: 'preset-coffee-1', type: 'coffee-machine', x: 60, y: 430, rotation: 0 },
      { id: 'preset-bookshelf-1', type: 'bookshelf', x: 665, y: 105, rotation: 0 },
      { id: 'preset-plant-1', type: 'tree-palm', x: 700, y: 360, rotation: 0 },
    ],
  },
  {
    id: 'sales-arena',
    name: 'Sales Arena',
    description: 'High-energy bullpen with a stage-like team huddle zone.',
    floorType: 'wood',
    items: [
      { id: 'arena-desk-1', type: 'desk-corner', x: 90, y: 150, rotation: 0 },
      { id: 'arena-desk-2', type: 'desk-corner', x: 240, y: 150, rotation: 0 },
      { id: 'arena-desk-3', type: 'desk-corner', x: 390, y: 150, rotation: 0 },
      { id: 'arena-desk-4', type: 'desk-corner', x: 540, y: 150, rotation: 0 },
      { id: 'arena-table-1', type: 'meeting-table', x: 270, y: 340, rotation: 0 },
      { id: 'arena-divider-1', type: 'divider-wide', x: 220, y: 300, rotation: 0 },
      { id: 'arena-vending-1', type: 'vending', x: 690, y: 335, rotation: 0 },
      { id: 'arena-server-1', type: 'server-rack', x: 700, y: 120, rotation: 0 },
      { id: 'arena-plant-1', type: 'plant-medium', x: 60, y: 395, rotation: 0 },
    ],
  },
  {
    id: 'garden-lab',
    name: 'Garden Lab',
    description: 'Relaxed hybrid office with greenery and breakout seating.',
    floorType: 'carpet',
    items: [
      { id: 'garden-desk-1', type: 'desk-wooden', x: 140, y: 165, rotation: 0 },
      { id: 'garden-desk-2', type: 'desk-wooden', x: 310, y: 165, rotation: 0 },
      { id: 'garden-sofa-1', type: 'sofa-large', x: 470, y: 300, rotation: 0 },
      { id: 'garden-rug-1', type: 'rug-large', x: 450, y: 290, rotation: 0 },
      { id: 'garden-window-1', type: 'window-wide', x: 510, y: 90, rotation: 0 },
      { id: 'garden-window-2', type: 'window-wide', x: 600, y: 90, rotation: 0 },
      { id: 'garden-tree-1', type: 'tree-large', x: 690, y: 340, rotation: 0 },
      { id: 'garden-bonsai-1', type: 'bonsai', x: 360, y: 135, rotation: 0 },
      { id: 'garden-cat-1', type: 'cat-bed', x: 560, y: 400, rotation: 0 },
    ],
  },
  {
    id: 'gaming-studio',
    name: 'Gaming Studio',
    description: 'Neon-lit creative space with arcade games and chill zones.',
    floorType: 'neon',
    items: [
      { id: 'gaming-desk-1', type: 'desk-corner', x: 100, y: 160, rotation: 0 },
      { id: 'gaming-desk-2', type: 'desk-corner', x: 250, y: 160, rotation: 0 },
      { id: 'gaming-chair-1', type: 'chair-gaming', x: 130, y: 220, rotation: 0 },
      { id: 'gaming-chair-2', type: 'chair-gaming', x: 280, y: 220, rotation: 0 },
      { id: 'gaming-arcade-1', type: 'arcade', x: 500, y: 150, rotation: 0 },
      { id: 'gaming-arcade-2', type: 'arcade', x: 580, y: 150, rotation: 0 },
      { id: 'gaming-sofa-1', type: 'sofa-large', x: 450, y: 350, rotation: 0 },
      { id: 'gaming-tv-1', type: 'tv-large', x: 420, y: 100, rotation: 0 },
      { id: 'gaming-vending-1', type: 'vending', x: 700, y: 280, rotation: 0 },
      { id: 'gaming-rug-1', type: 'rug-large', x: 430, y: 330, rotation: 0 },
    ],
  },
  {
    id: 'wellness-hub',
    name: 'Wellness Hub',
    description: 'Mindful workspace with yoga area and meditation corner.',
    floorType: 'grass',
    items: [
      { id: 'wellness-desk-1', type: 'desk-wooden', x: 120, y: 170, rotation: 0 },
      { id: 'wellness-desk-2', type: 'desk-wooden', x: 280, y: 170, rotation: 0 },
      { id: 'wellness-yoga-1', type: 'yoga-mat', x: 500, y: 200, rotation: 0 },
      { id: 'wellness-yoga-2', type: 'yoga-mat', x: 580, y: 200, rotation: 0 },
      { id: 'wellness-yoga-3', type: 'yoga-mat', x: 540, y: 260, rotation: 0 },
      { id: 'wellness-tree-1', type: 'tree-palm', x: 690, y: 140, rotation: 0 },
      { id: 'wellness-tree-2', type: 'tree-palm', x: 60, y: 380, rotation: 0 },
      { id: 'wellness-plant-1', type: 'plant-medium', x: 420, y: 130, rotation: 0 },
      { id: 'wellness-water-1', type: 'water-cooler', x: 700, y: 350, rotation: 0 },
      { id: 'wellness-bike-1', type: 'gym-bike', x: 500, y: 380, rotation: 0 },
    ],
  },
  {
    id: 'executive-suite',
    name: 'Executive Suite',
    description: 'Luxurious marble-floored office for leadership teams.',
    floorType: 'marble',
    items: [
      { id: 'exec-desk-1', type: 'desk-corner', x: 300, y: 150, rotation: 0 },
      { id: 'exec-chair-1', type: 'chair-office', x: 340, y: 210, rotation: 0 },
      { id: 'exec-reception-1', type: 'reception-desk', x: 100, y: 350, rotation: 0 },
      { id: 'exec-sofa-1', type: 'sofa-large', x: 500, y: 280, rotation: 0 },
      { id: 'exec-sofa-2', type: 'armchair', x: 450, y: 350, rotation: 0 },
      { id: 'exec-sofa-3', type: 'armchair', x: 620, y: 350, rotation: 0 },
      { id: 'exec-table-1', type: 'meeting-table', x: 500, y: 150, rotation: 0 },
      { id: 'exec-plant-1', type: 'tree-large', x: 700, y: 130, rotation: 0 },
      { id: 'exec-rug-1', type: 'rug-large', x: 480, y: 260, rotation: 0 },
      { id: 'exec-window-1', type: 'window-wide', x: 150, y: 95, rotation: 0 },
      { id: 'exec-window-2', type: 'window-wide', x: 240, y: 95, rotation: 0 },
    ],
  },
  {
    id: 'startup-loft',
    name: 'Startup Loft',
    description: 'Industrial concrete space with ping pong and open seating.',
    floorType: 'concrete',
    items: [
      { id: 'startup-desk-1', type: 'desk-modern', x: 100, y: 160, rotation: 0 },
      { id: 'startup-desk-2', type: 'desk-modern', x: 220, y: 160, rotation: 0 },
      { id: 'startup-desk-3', type: 'desk-modern', x: 340, y: 160, rotation: 0 },
      { id: 'startup-pingpong-1', type: 'ping-pong', x: 520, y: 180, rotation: 0 },
      { id: 'startup-sofa-1', type: 'sofa-small', x: 100, y: 350, rotation: 0 },
      { id: 'startup-coffee-1', type: 'coffee-machine', x: 700, y: 150, rotation: 0 },
      { id: 'startup-fridge-1', type: 'mini-fridge', x: 700, y: 220, rotation: 0 },
      { id: 'startup-whiteboard-1', type: 'whiteboard', x: 500, y: 95, rotation: 0 },
      { id: 'startup-bike-1', type: 'bicycle', x: 60, y: 430, rotation: 0 },
    ],
  },
  {
    id: 'holiday-office',
    name: 'Holiday Office',
    description: 'Festive seasonal setup with decorations and cheer.',
    floorType: 'wood',
    items: [
      { id: 'holiday-desk-1', type: 'desk-wooden', x: 150, y: 180, rotation: 0 },
      { id: 'holiday-desk-2', type: 'desk-wooden', x: 320, y: 180, rotation: 0 },
      { id: 'holiday-tree-1', type: 'christmas-tree', x: 550, y: 150, rotation: 0 },
      { id: 'holiday-snowman-1', type: 'snowman', x: 640, y: 200, rotation: 0 },
      { id: 'holiday-sofa-1', type: 'sofa-large', x: 450, y: 350, rotation: 0 },
      { id: 'holiday-rug-1', type: 'rug-large', x: 430, y: 330, rotation: 0 },
      { id: 'holiday-cake-1', type: 'cake', x: 700, y: 300, rotation: 0 },
      { id: 'holiday-gift-1', type: 'fireworks', x: 480, y: 100, rotation: 0 },
      { id: 'holiday-plant-1', type: 'plant-medium', x: 60, y: 380, rotation: 0 },
    ],
  },
];

const FLOOR_THEMES = {
  wood: {
    name: 'Wood',
    background: 'linear-gradient(135deg, #d4c4a8 0%, #c9b896 100%)',
    pattern: `
      linear-gradient(45deg, rgba(0,0,0,0.03) 25%, transparent 25%),
      linear-gradient(-45deg, rgba(0,0,0,0.03) 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, rgba(0,0,0,0.03) 75%),
      linear-gradient(-45deg, transparent 75%, rgba(0,0,0,0.03) 75%)
    `,
  },
  tile: {
    name: 'Tile',
    background: 'linear-gradient(135deg, #dbe4ec 0%, #c7d2dc 100%)',
    pattern: `
      linear-gradient(rgba(15,23,42,0.08) 1px, transparent 1px),
      linear-gradient(90deg, rgba(15,23,42,0.08) 1px, transparent 1px)
    `,
  },
  carpet: {
    name: 'Carpet',
    background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
    pattern: `
      radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
      radial-gradient(rgba(0,0,0,0.12) 1px, transparent 1px)
    `,
  },
  marble: {
    name: 'Marble',
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
    pattern: `
      linear-gradient(45deg, rgba(148,163,184,0.1) 25%, transparent 25%),
      linear-gradient(-45deg, rgba(148,163,184,0.15) 25%, transparent 25%)
    `,
  },
  concrete: {
    name: 'Concrete',
    background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
    pattern: `
      radial-gradient(rgba(255,255,255,0.05) 2px, transparent 2px),
      radial-gradient(rgba(0,0,0,0.1) 1px, transparent 1px)
    `,
  },
  grass: {
    name: 'Grass',
    background: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)',
    pattern: `
      radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)
    `,
  },
  neon: {
    name: 'Neon',
    background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
    pattern: `
      linear-gradient(rgba(139,92,246,0.1) 1px, transparent 1px),
      linear-gradient(90deg, rgba(236,72,153,0.1) 1px, transparent 1px)
    `,
  },
};

const WALL_THEMES = {
  warm: {
    name: 'Warm',
    wall: 'linear-gradient(to bottom, #8B7355 0%, #a08060 80%, #6b5344 100%)',
    trim: '#8B5A2B',
    panels: '#8b7355',
    edge: '#9c8465',
  },
  studio: {
    name: 'Studio',
    wall: 'linear-gradient(to bottom, #dbe4ec 0%, #cbd5e1 78%, #94a3b8 100%)',
    trim: '#64748b',
    panels: '#e2e8f0',
    edge: '#cbd5e1',
  },
  moss: {
    name: 'Moss',
    wall: 'linear-gradient(to bottom, #3f6212 0%, #4d7c0f 75%, #365314 100%)',
    trim: '#365314',
    panels: '#65a30d',
    edge: '#84cc16',
  },
  neon: {
    name: 'Neon',
    wall: 'linear-gradient(to bottom, #1e1b4b 0%, #312e81 75%, #0f172a 100%)',
    trim: '#7c3aed',
    panels: '#312e81',
    edge: '#8b5cf6',
  },
  sunset: {
    name: 'Sunset',
    wall: 'linear-gradient(to bottom, #7c2d12 0%, #c2410c 80%, #7c2d12 100%)',
    trim: '#ea580c',
    panels: '#fdba74',
    edge: '#fb923c',
  },
};

const SCENERY_THEMES = {
  losAngeles: {
    name: 'LA',
    description: 'Palm trees, skyline glow, and sunset freeway energy.',
  },
  miami: {
    name: 'Miami',
    description: 'Oceanfront towers, pastel coast, and neon nightlife.',
  },
  thailand: {
    name: 'Thailand',
    description: 'Tropical islands, temple silhouettes, and floating lantern vibes.',
  },
};

// ============================================
// FURNITURE SPRITE COMPONENT
// ============================================

const getFurnitureKind = (type) => {
  if (type.includes('desk') && !type.includes('phone')) return 'desk';
  if (type.includes('chair') || type.includes('armchair') || type.includes('stool')) return 'chair';
  if (type.includes('sofa')) return 'sofa';
  if (type.includes('book') || type.includes('cabinet') || type.includes('shelf')) return 'storage';
  if (type.includes('server')) return 'server';
  if (type.includes('coffee') || type.includes('vending') || type.includes('water') || type.includes('fridge') || type.includes('counter') || type.includes('bar-counter')) return 'refreshment';
  if (type.includes('tree') || type.includes('plant') || type.includes('cactus') || type.includes('bonsai') || type.includes('flower')) return 'plant';
  if (type.includes('rug') || type.includes('yoga-mat') || type.includes('skateboard')) return 'rug';
  if (type.includes('window') || type.includes('poster') || type.includes('clock') || type.includes('photo') || type.includes('whiteboard') || type.includes('tv') || type.includes('dart') || type.includes('hoop') || type.includes('camera')) return 'wall';
  if (type.includes('divider') || type.includes('wall') || type.includes('door') || type.includes('stairs')) return 'divider';
  if (type.includes('meeting-table') || type.includes('projector') || type.includes('printer') || type.includes('monitor') || type.includes('dining')) return 'table';
  if (type.includes('cat') || type.includes('dog') || type.includes('fish')) return 'pet';
  if (type.includes('lamp')) return 'lamp';
  if (type.includes('arcade')) return 'arcade';
  if (type.includes('ping-pong')) return 'pingpong';
  if (type.includes('foosball')) return 'foosball';
  if (type.includes('reception')) return 'reception';
  if (type.includes('bicycle') || type.includes('bike') || type.includes('gym-bike')) return 'bike';
  if (type.includes('yoga')) return 'yoga';
  if (type.includes('christmas') || type.includes('pumpkin') || type.includes('ghost') || type.includes('snowman') || type.includes('heart') || type.includes('fireworks') || type.includes('menorah')) return 'seasonal';
  if (type.includes('pizza') || type.includes('fruit') || type.includes('cake') || type.includes('sushi')) return 'food';
  if (type.includes('scooter') || type.includes('segway')) return 'vehicle';
  if (type.includes('laptop') || type.includes('phone') || type.includes('tablet') || type.includes('headset') || type.includes('speaker') || type.includes('router')) return 'electronic';
  return 'generic';
};

const renderFurnitureGraphic = (catalogItem, compact = false) => {
  const kind = getFurnitureKind(catalogItem.id);
  const baseClass = compact ? 'scale-[0.82]' : 'scale-100';

  switch (kind) {
    case 'desk':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-[34%] h-4 w-[78%] -translate-x-1/2 rounded-md bg-gradient-to-r from-[#7c5b3d] to-[#5b3f2a] shadow-[0_6px_0_rgba(62,39,20,0.7)]" />
          <div className="absolute left-[18%] top-[52%] h-[30%] w-[8%] rounded-sm bg-[#4b2e1a]" />
          <div className="absolute right-[18%] top-[52%] h-[30%] w-[8%] rounded-sm bg-[#4b2e1a]" />
          <div className="absolute left-1/2 top-[6%] h-[24%] w-[30%] -translate-x-1/2 rounded-md border border-white/10 bg-[#111827]">
            <div className="mx-auto mt-[12%] h-[45%] w-[70%] rounded-sm bg-[#38bdf8]" />
          </div>
          <div className="absolute left-1/2 top-[28%] h-[7%] w-[10%] -translate-x-1/2 rounded-sm bg-[#475569]" />
        </div>
      );
    case 'chair':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className={`absolute left-1/2 top-[46%] h-[26%] w-[48%] -translate-x-1/2 rounded-md ${catalogItem.id.includes('gaming') ? 'bg-[#7c3aed]' : 'bg-[#475569]'}`} />
          <div className={`absolute left-1/2 top-[18%] h-[30%] w-[38%] -translate-x-1/2 rounded-t-md ${catalogItem.id.includes('gaming') ? 'bg-[#8b5cf6]' : 'bg-[#475569]'}`} />
          <div className="absolute left-1/2 top-[70%] h-[16%] w-[6%] -translate-x-1/2 rounded-full bg-[#334155]" />
        </div>
      );
    case 'sofa':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute inset-x-[8%] top-[36%] h-[36%] rounded-2xl bg-[#a78bfa] shadow-lg" />
          <div className="absolute inset-x-[8%] top-[20%] h-[20%] rounded-t-2xl bg-[#8b5cf6]" />
          <div className="absolute left-[12%] top-[42%] h-[18%] w-[28%] rounded-xl bg-white/15" />
          <div className="absolute right-[12%] top-[42%] h-[18%] w-[28%] rounded-xl bg-white/15" />
        </div>
      );
    case 'storage':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-[14%] h-[72%] w-[42%] -translate-x-1/2 rounded-md bg-[#8b4513] shadow-md" />
          {[0, 1, 2].map((row) => (
            <div key={row} className="absolute left-1/2 flex -translate-x-1/2 gap-px" style={{ top: `${24 + row * 18}%` }}>
              {['#ef4444', '#3b82f6', '#22c55e', '#f59e0b'].map((color, index) => (
                <div key={color + index} className="w-1.5 rounded-sm" style={{ height: 7 + (index % 2) * 2, backgroundColor: color }} />
              ))}
            </div>
          ))}
        </div>
      );
    case 'server':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-[10%] h-[80%] w-[40%] -translate-x-1/2 rounded-md bg-[#111827] shadow-lg" />
          {[0, 1, 2, 3].map((row) => (
            <div key={row} className="absolute left-1/2 h-[10%] w-[30%] -translate-x-1/2 rounded-sm bg-[#334155]" style={{ top: `${18 + row * 15}%` }}>
              <div className="absolute right-[12%] top-[28%] flex gap-px">
                <div className="h-1 w-1 rounded-full bg-green-400" />
                <div className="h-1 w-1 rounded-full bg-sky-400" />
              </div>
            </div>
          ))}
        </div>
      );
    case 'refreshment':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-[16%] h-[66%] w-[36%] -translate-x-1/2 rounded-md bg-[#334155] shadow-lg" />
          <div className="absolute left-1/2 top-[26%] flex h-[14%] w-[20%] -translate-x-1/2 items-center justify-center rounded-sm bg-[#22c55e] text-[8px] font-bold text-black">
            {catalogItem.id.includes('water') ? 'H2O' : '☕'}
          </div>
          <div className="absolute left-1/2 top-[14%] h-[8%] w-[24%] -translate-x-1/2 rounded bg-[#0f172a]" />
        </div>
      );
    case 'plant':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute bottom-[8%] left-1/2 h-[18%] w-[28%] -translate-x-1/2 rounded-b-xl bg-[#92400e]" />
          <div className={`absolute left-[22%] rounded-full bg-[#22c55e] ${catalogItem.id.includes('tree') ? 'top-[6%] h-[44%] w-[44%]' : 'top-[18%] h-[30%] w-[30%]'}`} />
          <div className={`absolute rounded-full bg-[#16a34a] ${catalogItem.id.includes('tree') ? 'right-[18%] top-[2%] h-[38%] w-[38%]' : 'right-[22%] top-[18%] h-[26%] w-[26%]'}`} />
        </div>
      );
    case 'rug':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute inset-[12%] rounded-3xl border border-white/10 bg-[#8b5cf6]/20 shadow-inner" />
          <div className="absolute inset-[18%] rounded-[20px] border border-white/10" />
        </div>
      );
    case 'wall':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-[16%] h-[52%] w-[58%] -translate-x-1/2 rounded-xl border border-white/30 bg-gradient-to-b from-sky-200 to-sky-400 p-1 shadow-lg">
            <div className="grid h-full grid-cols-2 gap-1">
              <div className="rounded bg-white/25" />
              <div className="rounded bg-white/25" />
            </div>
          </div>
        </div>
      );
    case 'divider':
      return <div className={`relative h-full w-full ${baseClass}`}><div className="absolute left-1/2 top-1/2 h-3 w-[78%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-[#e2e8f0]/70" /></div>;
    case 'table':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-[28%] h-[28%] w-[68%] -translate-x-1/2 rounded-2xl bg-[#7c5b3d] shadow-lg" />
          <div className="absolute left-[24%] top-[54%] h-[22%] w-[6%] rounded-sm bg-[#4b2e1a]" />
          <div className="absolute right-[24%] top-[54%] h-[22%] w-[6%] rounded-sm bg-[#4b2e1a]" />
        </div>
      );
    case 'pet':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute bottom-[18%] left-1/2 h-[14%] w-[42%] -translate-x-1/2 rounded-full bg-[#92400e]/60" />
          <div className="absolute left-1/2 top-[18%] -translate-x-1/2 text-lg">{catalogItem.emoji}</div>
        </div>
      );
    case 'lamp':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-[16%] h-[16%] w-[32%] -translate-x-1/2 rounded-t-full bg-[#fef3c7] shadow-[0_0_18px_rgba(253,224,71,0.35)]" />
          <div className="absolute left-1/2 top-[32%] h-[32%] w-[4%] -translate-x-1/2 bg-[#92400e]" />
          <div className="absolute left-1/2 top-[62%] h-[6%] w-[20%] -translate-x-1/2 rounded-full bg-[#92400e]" />
        </div>
      );
    case 'arcade':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-[8%] h-[75%] w-[50%] -translate-x-1/2 rounded-t-xl bg-[#7c3aed] shadow-lg" />
          <div className="absolute left-1/2 top-[14%] h-[30%] w-[38%] -translate-x-1/2 rounded-md bg-[#111827]">
            <div className="mx-auto mt-[8%] h-[70%] w-[80%] rounded-sm bg-gradient-to-b from-[#22c55e] to-[#15803d]" />
          </div>
          <div className="absolute left-1/2 top-[48%] h-[8%] w-[20%] -translate-x-1/2 rounded-full bg-[#ef4444]" />
          <div className="absolute left-1/2 top-[58%] flex -translate-x-1/2 gap-1">
            <div className="h-2 w-2 rounded-full bg-[#fbbf24]" />
            <div className="h-2 w-2 rounded-full bg-[#3b82f6]" />
          </div>
        </div>
      );
    case 'pingpong':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-[30%] h-[8%] w-[85%] -translate-x-1/2 rounded-md bg-[#15803d] shadow-lg" />
          <div className="absolute left-1/2 top-[30%] h-[8%] w-[2%] -translate-x-1/2 bg-white" />
          <div className="absolute left-[20%] top-[50%] h-[26%] w-[6%] rounded-sm bg-[#4b2e1a]" />
          <div className="absolute right-[20%] top-[50%] h-[26%] w-[6%] rounded-sm bg-[#4b2e1a]" />
          <div className="absolute left-1/2 top-[24%] h-[4%] w-[6%] -translate-x-1/2 rounded-full bg-[#f97316]" />
        </div>
      );
    case 'foosball':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-[28%] h-[30%] w-[80%] -translate-x-1/2 rounded-lg bg-[#15803d] shadow-lg" />
          <div className="absolute left-1/2 top-[32%] h-[3%] w-[70%] -translate-x-1/2 bg-white/50" />
          <div className="absolute left-[18%] top-[55%] h-[22%] w-[5%] rounded-sm bg-[#4b2e1a]" />
          <div className="absolute right-[18%] top-[55%] h-[22%] w-[5%] rounded-sm bg-[#4b2e1a]" />
          {[25, 40, 55, 70].map((left) => (
            <div key={left} className="absolute top-[26%] h-[36%] w-[2%] bg-[#71717a]" style={{ left: `${left}%` }}>
              <div className="absolute left-1/2 top-[20%] h-3 w-3 -translate-x-1/2 rounded-full bg-[#3b82f6]" />
              <div className="absolute left-1/2 top-[60%] h-3 w-3 -translate-x-1/2 rounded-full bg-[#ef4444]" />
            </div>
          ))}
        </div>
      );
    case 'reception':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-[32%] h-[40%] w-[85%] -translate-x-1/2 rounded-xl bg-gradient-to-b from-[#7c5b3d] to-[#5b3f2a] shadow-lg" />
          <div className="absolute left-1/2 top-[24%] h-[12%] w-[70%] -translate-x-1/2 rounded-t-lg bg-[#8b6914]" />
          <div className="absolute left-[20%] top-[70%] h-[18%] w-[6%] rounded-sm bg-[#4b2e1a]" />
          <div className="absolute right-[20%] top-[70%] h-[18%] w-[6%] rounded-sm bg-[#4b2e1a]" />
          <div className="absolute left-1/2 top-[38%] h-[6%] w-[12%] -translate-x-1/2 rounded-full bg-[#fbbf24]" />
        </div>
      );
    case 'bike':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-[20%] top-[50%] h-[32%] w-[32%] rounded-full border-4 border-[#374151] bg-transparent" />
          <div className="absolute right-[20%] top-[50%] h-[32%] w-[32%] rounded-full border-4 border-[#374151] bg-transparent" />
          <div className="absolute left-[30%] top-[35%] h-[20%] w-[40%] rounded-sm bg-[#ef4444]" />
          <div className="absolute left-[38%] top-[25%] h-[12%] w-[4%] rounded-t-sm bg-[#374151]" />
          <div className="absolute right-[28%] top-[40%] h-[4%] w-[14%] -rotate-45 bg-[#374151]" />
        </div>
      );
    case 'yoga':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-1/2 h-[20%] w-[85%] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-gradient-to-r from-[#a855f7] to-[#ec4899] shadow-inner" />
        </div>
      );
    case 'seasonal':
      return (
        <div className={`flex h-full w-full items-center justify-center text-3xl ${baseClass} ${catalogItem.animated ? 'animate-bounce' : ''}`}>
          {catalogItem.emoji}
        </div>
      );
    case 'food':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-[30%] h-[35%] w-[70%] -translate-x-1/2 rounded-xl bg-[#f5f5f4] shadow-md" />
          <div className="absolute left-1/2 top-[35%] -translate-x-1/2 text-xl">{catalogItem.emoji}</div>
        </div>
      );
    case 'vehicle':
      return (
        <div className={`flex h-full w-full items-center justify-center text-2xl ${baseClass}`}>
          {catalogItem.emoji}
        </div>
      );
    case 'electronic':
      return (
        <div className={`relative h-full w-full ${baseClass}`}>
          <div className="absolute left-1/2 top-[25%] h-[45%] w-[55%] -translate-x-1/2 rounded-lg bg-[#1f2937] shadow-md">
            <div className="mx-auto mt-[10%] h-[65%] w-[80%] rounded-sm bg-[#38bdf8]" />
          </div>
          <div className="absolute left-1/2 top-[68%] h-[8%] w-[40%] -translate-x-1/2 rounded-b-sm bg-[#374151]" />
        </div>
      );
    default:
      return (
        <div className={`flex h-full w-full items-center justify-center rounded-lg bg-[#6b7280] text-2xl shadow-md ${baseClass}`}>
          {catalogItem.emoji}
        </div>
      );
  }
};

const FurnitureSprite = ({
  item,
  x,
  y,
  rotation = 0,
  isSelected = false,
  isPlacing = false,
  onClick,
  onDragStart,
}) => {
  const catalogItem = Object.values(FURNITURE_CATEGORIES)
    .flatMap(cat => cat.items)
    .find(i => i.id === item.type);

  if (!catalogItem) return null;

  const zIndex = Math.floor(y + (catalogItem.zOffset || 0));

  return (
    <div
      className={`absolute cursor-pointer transition-all duration-150 ${isPlacing ? 'opacity-70' : ''}`}
      style={{
        left: x,
        top: y,
        width: catalogItem.width,
        height: catalogItem.height,
        zIndex: zIndex,
        transform: `rotate(${rotation}deg)`,
      }}
      onClick={onClick}
      onMouseDown={onDragStart}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute -inset-2 border-2 border-dashed border-[#ff4d4d] rounded-lg animate-pulse" />
      )}

      {/* Furniture visual */}
      <div
        className={`w-full h-full flex items-center justify-center rounded-lg transition-transform
          ${isSelected ? 'scale-105' : 'hover:scale-102'}
          ${catalogItem.animated ? 'animate-bounce-subtle' : ''}
        `}
        style={{ boxShadow: isSelected ? '0 0 20px rgba(255, 77, 77, 0.5)' : '0 4px 8px rgba(0,0,0,0.12)' }}
      >
        {renderFurnitureGraphic(catalogItem)}
      </div>

      {/* Label */}
      {isSelected && (
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-white bg-black/70 px-2 py-0.5 rounded">
          {catalogItem.name}
        </div>
      )}
    </div>
  );
};

// ============================================
// FURNITURE CATALOG PANEL
// ============================================

const CatalogPanel = ({ onSelectItem, selectedCategory, setSelectedCategory }) => {
  return (
    <div className="w-64 bg-[#0d1424] border-r border-white/10 flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-white/10">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Package className="w-4 h-4 text-[#ff4d4d]" />
          Furniture Catalog
        </h3>
      </div>

      {/* Categories */}
      <div className="flex flex-wrap gap-1 p-2 border-b border-white/10">
        {Object.entries(FURNITURE_CATEGORIES).map(([key, category]) => {
          const Icon = category.icon;
          return (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`p-2 rounded-lg transition-colors ${
                selectedCategory === key
                  ? 'bg-[#ff4d4d] text-white'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
              title={category.name}
            >
              <Icon className="w-4 h-4" />
            </button>
          );
        })}
      </div>

      {/* Items */}
      <div className="flex-1 overflow-y-auto p-2">
        <div className="grid grid-cols-2 gap-2">
          {FURNITURE_CATEGORIES[selectedCategory]?.items.map((item) => (
            <button
              key={item.id}
              onClick={() => onSelectItem(item)}
              className="p-3 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 transition-all hover:border-[#ff4d4d]/50 text-center group"
            >
              <div className="mb-2 h-16 rounded-xl bg-black/20 p-1 group-hover:scale-105 transition-transform">
                {renderFurnitureGraphic(item, true)}
              </div>
              <p className="text-[10px] text-white/70 truncate">{item.name}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================
// TOOLBAR
// ============================================

const Toolbar = ({
  selectedItem,
  onRotate,
  onDelete,
  onDuplicate,
  onSave,
  onUndo,
  onRedo,
  isEditMode,
  setIsEditMode,
  zoom,
  setZoom,
  snapToGrid,
  setSnapToGrid,
  floorType,
  setFloorType,
  wallTheme,
  setWallTheme,
  sceneryTheme,
  setSceneryTheme,
  canUndo,
  canRedo,
}) => (
  <div className="h-12 bg-[#0d1424] border-b border-white/10 flex items-center justify-between px-4">
    <div className="flex items-center gap-2">
      {/* Edit/View Mode Toggle */}
      <button
        onClick={() => setIsEditMode(!isEditMode)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          isEditMode
            ? 'bg-[#ff4d4d] text-white'
            : 'bg-white/5 text-white/60 hover:bg-white/10'
        }`}
      >
        {isEditMode ? <Edit3 className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        {isEditMode ? 'Edit Mode' : 'View Mode'}
      </button>

      {/* Zoom controls */}
      <div className="flex items-center gap-1 ml-4">
        <button
          onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
          className="p-1.5 bg-white/5 hover:bg-white/10 rounded text-white/60"
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className="text-xs text-white/60 w-12 text-center">{Math.round(zoom * 100)}%</span>
        <button
          onClick={() => setZoom(Math.min(2, zoom + 0.1))}
          className="p-1.5 bg-white/5 hover:bg-white/10 rounded text-white/60"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      <button
        onClick={() => setSnapToGrid(!snapToGrid)}
        className={`ml-3 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
          snapToGrid
            ? 'bg-emerald-500/20 text-emerald-300'
            : 'bg-white/5 text-white/60 hover:bg-white/10'
        }`}
      >
        Snap {snapToGrid ? 'On' : 'Off'}
      </button>

      <div className="ml-2 flex items-center gap-1 rounded-lg bg-white/5 p-1">
        {Object.entries(FLOOR_THEMES).map(([key, value]) => (
          <button
            key={key}
            onClick={() => setFloorType(key)}
            className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
              floorType === key
                ? 'bg-[#ff4d4d] text-white'
                : 'text-white/60 hover:bg-white/10'
            }`}
          >
            {value.name}
          </button>
        ))}
      </div>

      <div className="ml-2 flex items-center gap-1 rounded-lg bg-white/5 p-1">
        {Object.entries(WALL_THEMES).map(([key, value]) => (
          <button
            key={key}
            onClick={() => setWallTheme(key)}
            className={`h-7 w-7 rounded-full border transition-all ${
              wallTheme === key
                ? 'scale-110 border-white'
                : 'border-white/15 hover:border-white/40'
            }`}
            style={{ background: value.wall }}
            title={value.name}
          />
        ))}
      </div>

      <div className="ml-2 flex items-center gap-1 rounded-lg bg-white/5 p-1">
        {Object.entries(SCENERY_THEMES).map(([key, value]) => (
          <button
            key={key}
            onClick={() => setSceneryTheme(key)}
            className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
              sceneryTheme === key
                ? 'bg-sky-500/20 text-sky-200'
                : 'text-white/60 hover:bg-white/10'
            }`}
            title={value.description}
          >
            {value.name}
          </button>
        ))}
      </div>
    </div>

    <div className="flex items-center gap-2">
      <button
        onClick={onUndo}
        disabled={!canUndo}
        className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/60 hover:text-white transition-colors disabled:cursor-not-allowed disabled:opacity-30"
        title="Undo"
      >
        <Undo2 className="w-4 h-4" />
      </button>
      <button
        onClick={onRedo}
        disabled={!canRedo}
        className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/60 hover:text-white transition-colors disabled:cursor-not-allowed disabled:opacity-30"
        title="Redo"
      >
        <Redo2 className="w-4 h-4" />
      </button>

      {/* Selection tools */}
      {selectedItem && isEditMode && (
        <>
          <button
            onClick={onDuplicate}
            className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/60 hover:text-white transition-colors"
            title="Duplicate"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={onRotate}
            className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/60 hover:text-white transition-colors"
            title="Rotate"
          >
            <RotateCw className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 bg-white/5 hover:bg-red-500/20 rounded-lg text-white/60 hover:text-red-400 transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <div className="w-px h-6 bg-white/10 mx-2" />
        </>
      )}

      {/* Save */}
      <button
        onClick={onSave}
        className="flex items-center gap-2 px-3 py-1.5 bg-[#ff4d4d] hover:bg-[#ff6666] text-white rounded-lg text-sm font-medium transition-colors"
      >
        <Save className="w-4 h-4" />
        Save Layout
      </button>
    </div>
  </div>
);

// ============================================
// MAIN OFFICE BUILDER
// ============================================

const OfficeBuilder = ({
  initialLayout = null,
  agents = [],
  activeEvents = {},
  onSaveLayout,
  officeName = 'My Office',
}) => {
  // State
  const [placedItems, setPlacedItems] = useState(initialLayout?.items || []);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [placingItem, setPlacingItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('desks');
  const [isEditMode, setIsEditMode] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [snapToGrid, setSnapToGrid] = useState(initialLayout?.snapToGrid !== false);
  const [floorType, setFloorType] = useState(initialLayout?.floorType || 'wood');
  const [wallTheme, setWallTheme] = useState(initialLayout?.appearance?.wallTheme || 'warm');
  const [sceneryTheme, setSceneryTheme] = useState(initialLayout?.appearance?.sceneryTheme || 'losAngeles');

  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });

  const pushHistory = useCallback((snapshot) => {
    setHistory(prev => [...prev.slice(-24), snapshot]);
    setFuture([]);
  }, []);

  // Canvas size
  useEffect(() => {
    const updateSize = () => {
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        setCanvasSize({ width: rect.width, height: rect.height });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Handle selecting an item from catalog
  const handleSelectCatalogItem = useCallback((catalogItem) => {
    if (!isEditMode) return;
    setPlacingItem(catalogItem);
    setSelectedItemId(null);
  }, [isEditMode]);

  // Handle placing item on canvas
  const handleCanvasClick = useCallback((e) => {
    if (!isEditMode) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const rawX = (e.clientX - rect.left) / zoom;
    const rawY = (e.clientY - rect.top) / zoom;
    const x = snapToGrid ? Math.round(rawX / 20) * 20 : rawX;
    const y = snapToGrid ? Math.round(rawY / 20) * 20 : rawY;

    if (placingItem) {
      pushHistory(placedItems);
      // Place new item
      const newItem = {
        id: `item_${Date.now()}`,
        type: placingItem.id,
        x: x - placingItem.width / 2,
        y: y - placingItem.height / 2,
        rotation: 0,
      };
      setPlacedItems(prev => [...prev, newItem]);
      setPlacingItem(null);
      setSelectedItemId(newItem.id);
    } else {
      // Deselect
      setSelectedItemId(null);
    }
  }, [placingItem, isEditMode, placedItems, pushHistory, snapToGrid, zoom]);

  // Handle item selection
  const handleItemClick = useCallback((e, itemId) => {
    e.stopPropagation();
    if (isEditMode) {
      setSelectedItemId(itemId);
      setPlacingItem(null);
    }
  }, [isEditMode]);

  // Handle item dragging
  const handleItemDrag = useCallback((itemId, e) => {
    if (!isEditMode) return;

    e.preventDefault();
    const item = placedItems.find(i => i.id === itemId);
    const catalogItem = Object.values(FURNITURE_CATEGORIES)
      .flatMap(cat => cat.items)
      .find(i => i.id === item.type);
    if (!item || !catalogItem) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const startItemX = item.x;
    const startItemY = item.y;

    pushHistory(placedItems);

    const handleMouseMove = (moveEvent) => {
      const dx = (moveEvent.clientX - startX) / zoom;
      const dy = (moveEvent.clientY - startY) / zoom;
      const nextX = Math.max(0, Math.min(canvasSize.width / zoom - catalogItem.width, startItemX + dx));
      const nextY = Math.max(0, Math.min(canvasSize.height / zoom - catalogItem.height, startItemY + dy));
      const snappedX = snapToGrid ? Math.round(nextX / 20) * 20 : nextX;
      const snappedY = snapToGrid ? Math.round(nextY / 20) * 20 : nextY;

      setPlacedItems(prev => prev.map(i =>
        i.id === itemId
          ? {
              ...i,
              x: snappedX,
              y: snappedY,
            }
          : i
      ));
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    setSelectedItemId(itemId);
  }, [canvasSize, isEditMode, placedItems, pushHistory, snapToGrid, zoom]);

  // Rotate selected item
  const handleRotate = useCallback(() => {
    if (!selectedItemId) return;
    pushHistory(placedItems);
    setPlacedItems(prev => prev.map(item =>
      item.id === selectedItemId
        ? { ...item, rotation: (item.rotation + 90) % 360 }
        : item
    ));
  }, [placedItems, pushHistory, selectedItemId]);

  // Delete selected item
  const handleDelete = useCallback(() => {
    if (!selectedItemId) return;
    pushHistory(placedItems);
    setPlacedItems(prev => prev.filter(item => item.id !== selectedItemId));
    setSelectedItemId(null);
  }, [placedItems, pushHistory, selectedItemId]);

  const handleDuplicate = useCallback(() => {
    if (!selectedItemId) return;
    const source = placedItems.find(item => item.id === selectedItemId);
    if (!source) return;
    pushHistory(placedItems);
    const duplicate = {
      ...source,
      id: `item_${Date.now()}`,
      x: source.x + 24,
      y: source.y + 24,
    };
    setPlacedItems(prev => [...prev, duplicate]);
    setSelectedItemId(duplicate.id);
  }, [placedItems, pushHistory, selectedItemId]);

  const handleUndo = useCallback(() => {
    if (history.length === 0) return;
    const previous = history[history.length - 1];
    setFuture(prev => [placedItems, ...prev].slice(0, 24));
    setHistory(prev => prev.slice(0, -1));
    setPlacedItems(previous);
    setSelectedItemId(null);
  }, [history, placedItems]);

  const handleRedo = useCallback(() => {
    if (future.length === 0) return;
    const [next, ...rest] = future;
    setHistory(prev => [...prev.slice(-24), placedItems]);
    setFuture(rest);
    setPlacedItems(next);
    setSelectedItemId(null);
  }, [future, placedItems]);

  const handleApplyPreset = useCallback((preset) => {
    pushHistory(placedItems);
    setPlacedItems(preset.items.map(item => ({ ...item })));
    setFloorType(preset.floorType || 'wood');
    setWallTheme(preset.wallTheme || 'warm');
    setSceneryTheme(preset.sceneryTheme || 'losAngeles');
    setSelectedItemId(null);
    setPlacingItem(null);
  }, [placedItems, pushHistory]);

  // Save layout
  const handleSave = useCallback(() => {
    const layout = {
      items: placedItems,
      floorType,
      snapToGrid,
      appearance: {
        floorType,
        wallTheme,
        sceneryTheme,
      },
      savedAt: new Date().toISOString(),
    };
    onSaveLayout?.(layout);
    // Also save to localStorage
    localStorage.setItem('claw9_office_layout', JSON.stringify(layout));
  }, [floorType, onSaveLayout, placedItems, sceneryTheme, snapToGrid, wallTheme]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isEditMode) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        handleDelete();
      } else if (e.key === 'r' || e.key === 'R') {
        handleRotate();
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        handleDuplicate();
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y') || ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'z')) {
        e.preventDefault();
        handleRedo();
      } else if (e.key === 'Escape') {
        setSelectedItemId(null);
        setPlacingItem(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleDelete, handleDuplicate, handleRedo, handleRotate, handleUndo, isEditMode]);

  const selectedItem = placedItems.find(i => i.id === selectedItemId);
  const officeScore = agents.length * 120 + placedItems.length * 35 + Object.keys(activeEvents).length * 50;
  const presetReadyCount = ASSET_PACKS.filter((pack) => pack.categories.includes(selectedCategory)).length;
  const floorTheme = FLOOR_THEMES[floorType] || FLOOR_THEMES.wood;
  const wallAppearance = WALL_THEMES[wallTheme] || WALL_THEMES.warm;

  return (
    <div className="h-full flex flex-col bg-[#050810]">
      {/* Toolbar */}
      <Toolbar
        selectedItem={selectedItem}
        onRotate={handleRotate}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        onSave={handleSave}
        onUndo={handleUndo}
        onRedo={handleRedo}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        zoom={zoom}
        setZoom={setZoom}
        snapToGrid={snapToGrid}
        setSnapToGrid={setSnapToGrid}
        floorType={floorType}
        setFloorType={setFloorType}
        wallTheme={wallTheme}
        setWallTheme={setWallTheme}
        sceneryTheme={sceneryTheme}
        setSceneryTheme={setSceneryTheme}
        canUndo={history.length > 0}
        canRedo={future.length > 0}
      />

      <div className="flex-1 flex min-h-0">
        {/* Catalog (only in edit mode) */}
        {isEditMode && (
          <div className="flex w-64 flex-col border-r border-white/10 bg-[#0d1424]">
            <CatalogPanel
              onSelectItem={handleSelectCatalogItem}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
            <div className="border-t border-white/10 p-3">
              <div className="mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-300" />
                <h4 className="text-xs font-semibold text-white">Asset Pack Angle</h4>
              </div>
              <div className="space-y-2">
                {ASSET_PACKS.map((pack) => (
                  <div key={pack.id} className="rounded-xl border border-white/10 bg-white/5 p-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] font-semibold text-white">{pack.name}</p>
                      <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-300">{pack.priceHint}</span>
                    </div>
                    <p className="mt-1 text-[10px] leading-4 text-white/55">{pack.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Canvas */}
        <div className="flex-1 overflow-auto p-4">
          <div className="mb-4 grid gap-3 lg:grid-cols-[1.5fr_1fr]">
            <div className="rounded-2xl border border-white/10 bg-[#0d1424]/90 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.32em] text-white/35">Preset Offices</p>
                  <h3 className="text-sm font-semibold text-white">Sellable starter scenes</h3>
                </div>
                <span className="rounded-full bg-[#ff4d4d]/15 px-3 py-1 text-[11px] font-medium text-[#ff9a9a]">{OFFICE_PRESETS.length} templates</span>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                {OFFICE_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => handleApplyPreset(preset)}
                    className="rounded-2xl border border-white/10 bg-white/5 p-3 text-left transition-all hover:border-[#ff4d4d]/40 hover:bg-white/10"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-200">{FLOOR_THEMES[preset.floorType]?.name || 'Custom'} floor</span>
                      <Check className="h-4 w-4 text-white/35" />
                    </div>
                    <p className="text-sm font-semibold text-white">{preset.name}</p>
                    <p className="mt-1 text-[11px] leading-4 text-white/55">{preset.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#0d1424]/90 p-4">
              <p className="text-[10px] uppercase tracking-[0.32em] text-white/35">Builder Score</p>
              <div className="mt-2 flex items-end justify-between">
                <div>
                  <p className="text-3xl font-bold text-white">{officeScore}</p>
                  <p className="text-xs text-white/50">Value of props, agents, and live activity</p>
                </div>
                <span className="rounded-full bg-sky-500/15 px-3 py-1 text-xs font-medium text-sky-300">{presetReadyCount} pack fit</span>
              </div>
              <div className="mt-4 space-y-2 text-xs text-white/60">
                <p>{placedItems.length} items placed</p>
                <p>{agents.length} agents to choreograph</p>
                <p>{Object.keys(activeEvents).length} live conversations visible</p>
              </div>
            </div>
          </div>

          <div className="mb-4 rounded-2xl border border-white/10 bg-[#0d1424]/90 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.32em] text-white/35">Outside World</p>
                <h3 className="mt-1 text-sm font-semibold text-white">Choose the 3D city backdrop</h3>
                <p className="mt-1 text-xs text-white/55">This affects the dollhouse scene outside the office walls.</p>
              </div>
              <span className="rounded-full bg-sky-500/15 px-3 py-1 text-xs font-medium text-sky-300">
                {SCENERY_THEMES[sceneryTheme]?.name || 'LA'}
              </span>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {Object.entries(SCENERY_THEMES).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => setSceneryTheme(key)}
                  className={`rounded-2xl border p-4 text-left transition-all ${
                    sceneryTheme === key
                      ? 'border-sky-400 bg-sky-500/10'
                      : 'border-white/10 bg-white/5 hover:border-white/25 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-white">{value.name}</p>
                    <span className="text-lg">
                      {key === 'losAngeles' ? '🌴' : key === 'miami' ? '🌊' : '🏝️'}
                    </span>
                  </div>
                  <p className="mt-2 text-xs leading-5 text-white/55">{value.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div
            ref={canvasRef}
            className="relative rounded-xl overflow-hidden cursor-crosshair"
            style={{
              width: '100%',
              height: '100%',
              minHeight: 500,
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              background: floorTheme.background,
            }}
            onClick={handleCanvasClick}
          >
            {/* Floor pattern */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: floorTheme.pattern,
                backgroundSize: floorType === 'carpet' ? '14px 14px' : '40px 40px',
                backgroundPosition: '0 0, 0 20px, 20px -20px, -20px 0px',
              }}
            />

            {/* Wall */}
            <div
              className="absolute top-0 left-0 right-0 h-24"
              style={{
                background: wallAppearance.wall,
              }}
            >
              {/* Office name */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-[#1f2937] text-white px-4 py-2 rounded-lg shadow-lg">
                <span className="font-bold text-sm">{officeName}</span>
              </div>
            </div>

            {/* Grid overlay (edit mode) */}
            {isEditMode && (
              <div
                className="absolute inset-0 pointer-events-none opacity-20"
                style={{
                  backgroundImage: 'linear-gradient(rgba(255,77,77,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,77,77,0.3) 1px, transparent 1px)',
                  backgroundSize: '20px 20px',
                }}
              />
            )}

            {/* Placed items */}
            {placedItems
              .sort((a, b) => a.y - b.y) // Sort by Y for proper layering
              .map((item) => (
                <FurnitureSprite
                  key={item.id}
                  item={item}
                  x={item.x}
                  y={item.y}
                  rotation={item.rotation}
                  isSelected={item.id === selectedItemId}
                  onClick={(e) => handleItemClick(e, item.id)}
                  onDragStart={(e) => handleItemDrag(item.id, e)}
                />
              ))}

            {/* Placing preview */}
            {placingItem && (
              <div
                className="fixed pointer-events-none opacity-50"
                style={{
                  width: placingItem.width,
                  height: placingItem.height,
                }}
              >
                <div className="w-full h-full bg-[#ff4d4d]/30 rounded-lg flex items-center justify-center text-2xl">
                  {placingItem.emoji}
                </div>
              </div>
            )}

            {/* Agents (as characters) */}
            {agents.map((agent, index) => {
              const isActive = !!activeEvents[agent.id];
              // Position agents at desks or random spots
              const agentX = 100 + (index % 4) * 150;
              const agentY = 180 + Math.floor(index / 4) * 120;

              return (
                <div
                  key={agent.id}
                  className="absolute transition-all"
                  style={{
                    left: agentX,
                    top: agentY,
                    zIndex: agentY + 100,
                  }}
                >
                  {/* Character */}
                  <div
                    className={`w-8 h-10 rounded-t-full flex items-end justify-center text-lg
                      ${isActive ? 'animate-bounce' : ''}
                    `}
                    style={{
                      backgroundColor: ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6'][index % 5],
                    }}
                  >
                    <span>{agent.emoji || '🧑'}</span>
                  </div>

                  {/* Name */}
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <span
                      className="text-[9px] px-1.5 py-0.5 rounded font-medium"
                      style={{
                        backgroundColor: isActive ? '#22c55e' : 'rgba(0,0,0,0.7)',
                        color: 'white',
                      }}
                    >
                      {agent.name}
                    </span>
                  </div>

                  {/* Chat bubble */}
                  {isActive && (
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 animate-bounce">
                      💬
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Instructions (edit mode) */}
      {isEditMode && (
        <div className="px-4 py-2 bg-[#0d1424] border-t border-white/10 text-xs text-white/50 flex items-center gap-4">
          <span>Click catalog to select → Click canvas to place</span>
          <span>•</span>
          <span>Drag to move items</span>
          <span>•</span>
          <span><kbd className="px-1 bg-white/10 rounded">Cmd/Ctrl+D</kbd> Duplicate</span>
          <span>•</span>
          <span><kbd className="px-1 bg-white/10 rounded">Cmd/Ctrl+Z</kbd> Undo</span>
          <span>•</span>
          <span><kbd className="px-1 bg-white/10 rounded">R</kbd> Rotate</span>
          <span>•</span>
          <span><kbd className="px-1 bg-white/10 rounded">Del</kbd> Delete</span>
        </div>
      )}
    </div>
  );
};

OfficeBuilder.propTypes = {
  initialLayout: PropTypes.object,
  agents: PropTypes.array,
  activeEvents: PropTypes.object,
  onSaveLayout: PropTypes.func,
  officeName: PropTypes.string,
};

export default OfficeBuilder;
